import { useCallback } from 'react';
import { DayPilot } from '@daypilot/daypilot-lite-react';
import { DOTS_SVG, FR_DAYS_SHORT } from './constants';
import { lightenColor } from './utils';
import { initials } from '../shared/ui/InitialsAvatar';

export function useSchedulerRenderers({ projects, tasks, currentView, publicHolidays, isAdmin, resources, resourceUtilization }) {

  const onBeforeEventRender = useCallback((args) => {
    const color = args.data.barColor || args.data.backColor || '#6fa8dc';
    args.data.barColor = color;
    args.data.backColor = lightenColor(color, 0.6);
    args.data.borderColor = color;

    const text = args.data.text || '';
    const wRaw = args.data._meta?.workload;
    const workload = Number.isFinite(wRaw) ? wRaw : 100;
    const startStr = new DayPilot.Date(args.data.start).toString('dd/MM/yyyy');
    const endStr = new DayPilot.Date(args.data.end).toString('dd/MM/yyyy');
    const projectId = args.data._meta?.projectId;
    const projectName = projectId ? (projects.find((p) => p.id === projectId)?.name || '') : '';
    const taskId = args.data._meta?.taskId;
    const taskName = taskId ? (() => { const found = (tasks || []).find((t) => (t.data || t).id === taskId); const d = found?.data || found; return d?.title || d?.name || ''; })() : '';
    const notes = args.data._meta?.notes || '';
    const author = args.data._meta?.author || '';

    args.data.bubbleHtml =
      '<div style="max-width:300px;padding:4px 6px;font-size:13px;line-height:1.5">' +
      '<b style="font-size:14px">' + text + '</b>' +
      '<br/><span style="color:#555">' + startStr + ' → ' + endStr + '</span>' +
      '<br/><span style="color:#555">Charge : </span><b>' + workload + '%</b>' +
      (projectName ? '<br/><span style="color:#555">Projet : </span><b>' + projectName + '</b>' : '') +
      (taskName ? '<br/><span style="color:#555">Tâche : </span><b>' + taskName + '</b>' : '') +
      (author ? '<br/><span style="color:#888">Par ' + author + '</span>' : '') +
      (notes ? '<br/><i style="color:#888">' + notes + '</i>' : '') +
      '</div>';

    const wBadge = '<span style="display:inline-block;font-size:10px;font-weight:700;background:' + color + ';color:#fff;padding:1px 5px;border-radius:8px;margin-right:5px;line-height:16px;vertical-align:middle">' + workload + '%</span>';

    args.data.html = '<div style="padding-left:12px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;line-height:38px">' + wBadge + text + '</div>';

    args.data.areas = [
      { left: 0, top: 0, width: 5, bottom: 0, backColor: color },
      {
        right: 2, top: 'calc(50% - 10px)', width: 20, height: 20,
        style: 'cursor:pointer;',
        backColor: '#ffffff66',
        borderRadius: '50%',
        padding: 2,
        html: '<div style="display:flex;align-items:center;justify-content:center;width:100%;height:100%">' + DOTS_SVG + '</div>',
        action: 'ContextMenu',
        toolTip: 'Menu',
      },
    ];
  }, [projects, tasks, currentView]);

  const onBeforeCellRender = useCallback((args) => {
    const resId = args.cell.resource;

    if (resId === '_histogram' || resId === '_utilization') {
      const scheduler = args.control;
      const realRows = scheduler.rows.all().filter((r) => r.data.frozen !== 'top');
      const max = realRows.length;
      const eventsInRange = scheduler.events.forRange(args.cell.start, args.cell.end);
      let totalLoad = 0;
      for (const ev of eventsInRange) {
        const evW = ev.data._meta?.workload;
        totalLoad += (Number.isFinite(evW) ? evW : 100) / 100;
      }
      const pct = max > 0 ? totalLoad / max : 0;

      if (resId === '_histogram') {
        args.cell.properties.backColor = '#fafafa';
        if (totalLoad > 0) {
          const cellH = scheduler.eventHeight - 1;
          const barH = Math.min(pct, 1) * cellH;
          const barCol = pct >= 0.85 ? '#e06666' : pct >= 0.5 ? '#f6b26b' : '#6aa84f';
          args.cell.properties.areas = [{
            bottom: 1, height: barH, left: 0, right: 1,
            backColor: barCol,
            style: 'box-sizing:border-box;border:1px solid ' + barCol + '88;border-radius:2px 2px 0 0;',
          }];
        }
      } else {
        const txt = Math.round(100 * pct);
        const col = pct >= 0.85 ? '#cc0000' : pct >= 0.5 ? '#cc6600' : '#2e7d32';
        args.cell.properties.backColor = '#fafafa';
        args.cell.properties.areas = [{
          left: 0, top: 0, right: 0, bottom: 0,
          style: 'display:flex;justify-content:center;align-items:center;font-size:10px;font-weight:700;color:' + col,
          text: txt + '%',
        }];
      }
      return;
    }

    const cellDate = args.cell.start.toString('yyyy-MM-dd');
    const today = DayPilot.Date.today();
    if (args.cell.start <= today && today < args.cell.end) {
      args.cell.properties.backColor = '#eef6ff';
    }

    const holiday = publicHolidays ? publicHolidays.find((h) => {
      const hDate = (h.date || '').substring(0, 10);
      const d = cellDate.substring(0, 10);
      if (hDate === d) return true;
      if (h.recurring && hDate.substring(5) === d.substring(5)) return true;
      return false;
    }) : null;

    if (holiday) {
      args.cell.properties.backColor = '#f3f3f3';
      args.cell.properties.toolTip = holiday.name;
      if (!isAdmin) args.cell.properties.disabled = true;
    }

    const dow = args.cell.start.getDayOfWeek();
    if (dow === 0 || dow === 6) {
      args.cell.properties.backColor = holiday ? '#e0e0e0' : '#f0f0f0';
      if (!isAdmin) args.cell.properties.disabled = true;
    }

  }, [publicHolidays, isAdmin]);

  const onBeforeTimeHeaderRender = useCallback((args) => {
    if (currentView === 'year') {
      if (args.header.level === 0) {
        args.header.backColor = '#faf5ff';
        args.header.html = '<div style="text-align:center;font-size:12px;font-weight:600;color:#7e22ce;text-transform:capitalize">' + args.header.text + '</div>';
      }
      if (args.header.level === 1) {
        args.header.html = 'S' + args.header.text;
      }
      if (args.header.level === 2) {
        const date = args.header.start;
        const dow = date.getDayOfWeek();
        if (dow === 0 || dow === 6) {
          args.header.backColor = '#f0f0f0';
          args.header.areas = [{ left: 0, top: 0, right: 0, height: 4, backColor: '#a78bfa' }];
        }
      }
    } else {
      if (args.header.level === 0) {
        args.header.backColor = '#faf5ff';
        args.header.html = '<div style="text-align:center;font-size:12px;font-weight:600;color:#7e22ce;text-transform:capitalize">' + args.header.text + '</div>';
      }
      if (args.header.level === 1 && args.header.start) {
        const date = args.header.start;
        const dow = date.getDayOfWeek();
        const dayAbbr = FR_DAYS_SHORT[dow];
        const dayNum = parseInt(date.toString('d'), 10);
        const today = DayPilot.Date.today();
        const isToday = date.toString('yyyy-MM-dd') === today.toString('yyyy-MM-dd');
        const isWeekend = dow === 0 || dow === 6;

        const numColor = isToday ? '#1d4ed8' : isWeekend ? '#9ca3af' : '#1f2937';
        const bgColor = isToday ? '#dbeafe' : isWeekend ? '#e5e7eb' : '';
        if (bgColor) args.header.backColor = bgColor;

        args.header.html =
          '<div style="text-align:center;line-height:1.3;padding:1px 0">' +
          '<div style="font-size:10px;color:#9ca3af;font-weight:600">' + dayAbbr + '</div>' +
          '<div style="font-size:13px;font-weight:700;color:' + numColor + '">' + dayNum + '</div>' +
          '</div>';
      }
    }

  }, [currentView]);

  const onBeforeRowHeaderRender = useCallback((args) => {
    if (args.row.id === '_histogram') {
      args.row.html = '<div style="display:flex;align-items:center;justify-content:center;height:100%;font-size:11px;font-weight:700;color:#666;gap:4px"><span style="font-size:13px">📊</span> Charge</div>';
      return;
    }
    if (args.row.id === '_utilization') {
      args.row.html = '<div style="display:flex;align-items:center;justify-content:center;height:100%;font-size:11px;font-weight:700;color:#666;gap:4px"><span style="font-size:13px">📈</span> Taux</div>';
      return;
    }
    const resource = resources.find((r) => String(r.id) === String(args.row.id));
    if (!resource) return;
    const cap = resource.dailyCapacity || 7;
    // Pastille d'initiales (pas de photo) — HTML injecté par DayPilot, d'où le style inline.
    const avatarHtml = '<div style="width:37px;height:37px;border-radius:50%;background:#e3e3fd;flex-shrink:0;display:flex;align-items:center;justify-content:center;font-size:14px;font-weight:700;color:#4a3fc7">' + initials(resource.name || '?') + '</div>';

    const pct = resourceUtilization?.[String(args.row.id)] ?? 0;
    let pctBg, pctColor;
    if (pct >= 90) { pctBg = '#fee2e2'; pctColor = '#991b1b'; }
    else if (pct >= 70) { pctBg = '#fef3c7'; pctColor = '#92400e'; }
    else { pctBg = '#dcfce7'; pctColor = '#166534'; }

    args.row.html =
      '<div style="display:flex;align-items:center;padding:8px 13px;height:100%;gap:13px">' +
      avatarHtml +
      '<div style="flex:1;min-width:0;overflow:hidden">' +
        '<div style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-size:16px;font-weight:600;color:#1f2937">' + resource.name + '</div>' +
        '<div style="display:flex;gap:8px;margin-top:4px">' +
          '<span style="font-size:12px;background:#e9d5ff;color:#7e22ce;padding:2px 8px;border-radius:11px;line-height:21px;font-weight:600">' + cap + 'h/j</span>' +
          '<span style="font-size:12px;background:' + pctBg + ';color:' + pctColor + ';padding:2px 8px;border-radius:11px;line-height:21px;font-weight:700">' + pct + '%</span>' +
        '</div>' +
      '</div>' +
      '</div>';
  }, [resources, resourceUtilization]);

  return { onBeforeEventRender, onBeforeCellRender, onBeforeTimeHeaderRender, onBeforeRowHeaderRender };
}
